import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { cn } from '../../../lib/cn';
import { leadSchema, type LeadInput, type LeadRow, type LeadSourceRow } from './leads.schema';
import styles from './leads.module.css';

const DEFAULT_VALUES: LeadInput = {
  full_name: '',
  company_name: '',
  email: '',
  phone: '',
  source_id: '',
  interest: '',
  score: undefined,
  status: 'new',
  received_at: toDateTimeLocal(new Date().toISOString()),
  notes: '',
};

function toDateTimeLocal(iso: string): string {
  const date = new Date(iso);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function valuesFromLead(lead: LeadRow | null): LeadInput {
  if (!lead) {
    return {
      ...DEFAULT_VALUES,
      received_at: toDateTimeLocal(new Date().toISOString()),
    };
  }

  return {
    full_name: lead.full_name,
    company_name: lead.company_name ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    source_id: lead.source_id ?? '',
    interest: lead.interest ?? '',
    score: lead.score ?? undefined,
    status: lead.status === 'converted' ? 'qualified' : (lead.status as LeadInput['status']),
    received_at: toDateTimeLocal(lead.received_at),
    notes: lead.notes ?? '',
  };
}

interface LeadFormProps {
  lead: LeadRow | null;
  sources: LeadSourceRow[];
  formId: string;
  disabled?: boolean;
  onSubmit: (values: LeadInput) => void | Promise<void>;
}

export function LeadForm({ lead, sources, formId, disabled = false, onSubmit }: LeadFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LeadInput>({
    resolver: zodResolver(leadSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(valuesFromLead(lead));
  }, [lead, reset]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.field}>
        <label htmlFor="lead-full-name">שם מלא</label>
        <input
          id="lead-full-name"
          type="text"
          className={cn(styles.input, errors.full_name && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('full_name')}
        />
        {errors.full_name ? <p className={styles.error}>{errors.full_name.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="lead-company-name">חברה</label>
        <input
          id="lead-company-name"
          type="text"
          className={cn(styles.input, errors.company_name && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('company_name')}
        />
        {errors.company_name ? <p className={styles.error}>{errors.company_name.message}</p> : null}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="lead-email">אימייל</label>
          <input
            id="lead-email"
            type="email"
            dir="ltr"
            className={cn(styles.input, errors.email && styles.invalid)}
            disabled={disabled || isSubmitting}
            {...register('email')}
          />
          {errors.email ? <p className={styles.error}>{errors.email.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="lead-phone">טלפון</label>
          <input
            id="lead-phone"
            type="tel"
            dir="ltr"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('phone')}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="lead-source">מקור</label>
          <select
            id="lead-source"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('source_id')}
          >
            <option value="">ללא</option>
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
          {errors.source_id ? <p className={styles.error}>{errors.source_id.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="lead-status">סטטוס</label>
          <select
            id="lead-status"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('status')}
          >
            <option value="new">חדש</option>
            <option value="contacted">יצרנו קשר</option>
            <option value="qualified">הוסמך</option>
            <option value="disqualified">לא רלוונטי</option>
          </select>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="lead-interest">עניין</label>
        <input
          id="lead-interest"
          type="text"
          className={cn(styles.input, errors.interest && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('interest')}
        />
        {errors.interest ? <p className={styles.error}>{errors.interest.message}</p> : null}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="lead-score">ציון</label>
          <input
            id="lead-score"
            type="number"
            min={0}
            max={100}
            dir="ltr"
            className={cn(styles.input, errors.score && styles.invalid)}
            disabled={disabled || isSubmitting}
            {...register('score', {
              setValueAs: (value) => {
                if (value === '') return undefined;
                return Number(value);
              },
            })}
          />
          {errors.score ? <p className={styles.error}>{errors.score.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="lead-received-at">נקלט</label>
          <input
            id="lead-received-at"
            type="datetime-local"
            dir="ltr"
            className={cn(styles.input, errors.received_at && styles.invalid)}
            disabled={disabled || isSubmitting}
            {...register('received_at')}
          />
          {errors.received_at ? <p className={styles.error}>{errors.received_at.message}</p> : null}
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="lead-notes">הערות</label>
        <textarea
          id="lead-notes"
          rows={5}
          className={cn(styles.input, styles.textarea, errors.notes && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('notes')}
        />
        {errors.notes ? <p className={styles.error}>{errors.notes.message}</p> : null}
      </div>
    </form>
  );
}
